/**
 * Placeholder humanoid built from Babylon primitives — torso + head + limbs.
 * Doesn't pretend to be the final art. It's the «proof the pipeline works»
 * that survives until proper Quaternius / KayKit GLBs are imported in
 * Phase 1+. Limbs swing procedurally during walk. Tinted by side colour
 * + sub-faction accent.
 *
 * Phase 11: exposes attachment TransformNodes (head, mainhand, offhand,
 * back, shoulders, feet) so equipment meshes can be parented to them.
 * `setEquipment(map)` builds visible gear primitives keyed by item slot
 * + weaponKind, tinted by ItemDef.tint. Disposes old meshes on swap.
 */

import {
  Color3,
  Mesh,
  MeshBuilder,
  Scene,
  StandardMaterial,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { ITEMS, type ItemDef, type Slot } from '../data/equipment.js';
import { COSMETICS } from '../data/cosmetic-shop.js';

export interface PlaceholderCharacterRig {
  root: TransformNode;
  setColours: (skin: Color3, hair: Color3, accent: Color3) => void;
  setMoving: (moving: boolean) => void;
  /** Update animation phase — call from render loop. */
  update: (dt: number) => void;
  /** Replace visible equipment. Pass full slot map; rig diffs vs current. */
  setEquipment: (equipped: Partial<Record<Slot, string>>) => void;
  /** Replace cosmetic overlays (cape + pet from the shop). */
  setCosmetics: (eq: { cape: string | null; pet: string | null }) => void;
  /** Trigger a one-shot attack swing — main hand sweeps forward over ~0.4s. */
  triggerAttack: () => void;
  dispose: () => void;
}

/**
 * R232q75: Невидимый rig-аnchor — Sirus rig'а ещё нет, но scene нужен корневой
 * TransformNode для будущей подмены без сломанных типов. НЕ рендерит мешей,
 * НЕ показывает «коричневую кучу». Используется в CharacterCreator preview;
 * пока anchor живёт — React-слой LoadingScreen рисует heraldic-loading-screen
 * поверх Babylon canvas.
 *
 * World.tsx пока остаётся на createPlaceholderCharacter — его слой будет
 * переписан в Sirus 1:1 UI rebuild фазе (отдельный roadmap).
 */
export function createInvisibleCharacterAnchor(scene: Scene): PlaceholderCharacterRig {
  const root = new TransformNode('char-root-invisible', scene);
  const noop = () => undefined;
  return {
    root,
    setColours: noop,
    setMoving: noop,
    update: noop,
    setEquipment: noop,
    setCosmetics: noop,
    triggerAttack: noop,
    dispose: () => root.dispose(),
  };
}

const ITEM_LOOKUP = new Map(ITEMS.map((i) => [i.id, i]));
const COSMETIC_LOOKUP = new Map(COSMETICS.map((c) => [c.id, c]));

export function createPlaceholderCharacter(scene: Scene, opts?: {
  body?: 'slim' | 'medium' | 'broad';
  gender?: 'male' | 'female';
  scale?: number;
}): PlaceholderCharacterRig {
  const body = opts?.body ?? 'medium';
  const gender = opts?.gender ?? 'male';
  const scale = opts?.scale ?? 1.0;

  const widthMult = body === 'slim' ? 0.85 : body === 'broad' ? 1.2 : 1.0;
  const heightMult = gender === 'female' ? 0.95 : 1.0;

  const root = new TransformNode('char-root', scene);

  const skinMat = new StandardMaterial('skin', scene);
  skinMat.diffuseColor = new Color3(0.95, 0.78, 0.65);
  skinMat.specularColor = new Color3(0.05, 0.05, 0.05);

  const hairMat = new StandardMaterial('hair', scene);
  hairMat.diffuseColor = new Color3(0.25, 0.15, 0.09);
  hairMat.specularColor = new Color3(0.1, 0.1, 0.1);

  const accentMat = new StandardMaterial('accent', scene);
  accentMat.diffuseColor = new Color3(0.2, 0.25, 0.4);
  accentMat.specularColor = new Color3(0.05, 0.05, 0.05);
  accentMat.emissiveColor = new Color3(0.05, 0.07, 0.12);

  const trouserMat = new StandardMaterial('trouser', scene);
  trouserMat.diffuseColor = new Color3(0.12, 0.15, 0.22);

  // Torso — slightly tapered cylinder reads more organically than a box.
  const torso = MeshBuilder.CreateCylinder('torso', {
    diameterTop: 0.46 * widthMult,
    diameterBottom: 0.55 * widthMult,
    height: 0.78 * heightMult,
    tessellation: 14,
  }, scene);
  torso.scaling = new Vector3(1, 1, 0.65); // squash front-back for shoulder feel
  torso.position.y = 1.28 * heightMult;
  torso.material = accentMat;
  torso.parent = root;

  // Hips — slightly narrower bottom block helps the silhouette read.
  const hips = MeshBuilder.CreateBox('hips', {
    width: 0.4 * widthMult,
    height: 0.18 * heightMult,
    depth: 0.32,
  }, scene);
  hips.position.y = 0.95 * heightMult;
  hips.material = trouserMat;
  hips.parent = root;

  // Neck — small cylinder bridging torso to head.
  const neck = MeshBuilder.CreateCylinder('neck', {
    diameterTop: 0.16,
    diameterBottom: 0.20,
    height: 0.16 * heightMult,
    tessellation: 10,
  }, scene);
  neck.position.y = 1.74 * heightMult;
  neck.material = skinMat;
  neck.parent = root;

  // Head.
  const head = MeshBuilder.CreateSphere('head', { diameter: 0.34, segments: 16 }, scene);
  head.position.y = 1.92 * heightMult;
  head.scaling = new Vector3(1, 1.08, 0.96);
  head.material = skinMat;
  head.parent = root;

  // Hair cap — sits slightly higher so it reads as hair on top of the head.
  const hair = MeshBuilder.CreateSphere('hair', { diameter: 0.36, segments: 16 }, scene);
  hair.position.y = 2.00 * heightMult;
  hair.scaling = new Vector3(1.04, 0.72, 1.04);
  hair.material = hairMat;
  hair.parent = root;

  // Eyes (placeholder dark dots) and a small mouth dot for face anchor.
  const eyeMat = new StandardMaterial('eye', scene);
  eyeMat.diffuseColor = new Color3(0.08, 0.1, 0.15);
  for (const sign of [-1, 1]) {
    const eye = MeshBuilder.CreateSphere('eye', { diameter: 0.05, segments: 8 }, scene);
    eye.position = new Vector3(0.07 * sign, 1.94 * heightMult, -0.16);
    eye.material = eyeMat;
    eye.parent = root;
  }
  const mouthMat = new StandardMaterial('mouth', scene);
  mouthMat.diffuseColor = new Color3(0.45, 0.20, 0.20);
  const mouth = MeshBuilder.CreateBox('mouth', { width: 0.07, height: 0.012, depth: 0.01 }, scene);
  mouth.position = new Vector3(0, 1.86 * heightMult, -0.17);
  mouth.material = mouthMat;
  mouth.parent = root;

  // Arms — left and right, pivot on shoulder for animation swing.
  const armMeshes: { pivot: TransformNode; phase: number }[] = [];
  const handAnchors: { left: TransformNode; right: TransformNode } = {
    left: new TransformNode('hand-left', scene),
    right: new TransformNode('hand-right', scene),
  };
  // Shoulder caps — round bumps where the arms attach. Reads as a real
  // shoulder rather than a clean cube edge.
  for (const sign of [-1, 1]) {
    const cap = MeshBuilder.CreateSphere(`shoulder-${sign}`, { diameter: 0.28, segments: 10 }, scene);
    cap.position = new Vector3(0.34 * sign * widthMult, 1.6 * heightMult, 0);
    cap.material = accentMat;
    cap.parent = root;
  }

  for (const sign of [-1, 1]) {
    const pivot = new TransformNode(`arm-pivot-${sign}`, scene);
    pivot.position = new Vector3(0.32 * sign * widthMult, 1.58 * heightMult, 0);
    pivot.parent = root;
    // Upper arm — tapered cylinder.
    const arm = MeshBuilder.CreateCylinder(`arm-${sign}`, {
      diameterTop: 0.13,
      diameterBottom: 0.11,
      height: 0.62 * heightMult,
      tessellation: 10,
    }, scene);
    arm.position.y = -0.32 * heightMult;
    arm.material = skinMat;
    arm.parent = pivot;
    // Hand — small sphere at end.
    const hand = MeshBuilder.CreateSphere(`hand-${sign}`, { diameter: 0.16, segments: 10 }, scene);
    hand.position.y = -0.66 * heightMult;
    hand.material = skinMat;
    hand.parent = pivot;
    // Hand anchor at end of arm (used by equipment).
    const handAnchor = sign > 0 ? handAnchors.right : handAnchors.left;
    handAnchor.parent = pivot;
    handAnchor.position = new Vector3(0, -0.66 * heightMult, 0.05);
    armMeshes.push({ pivot, phase: sign > 0 ? 0 : Math.PI });
  }

  // Legs — pivot on hip.
  const legMeshes: { pivot: TransformNode; phase: number }[] = [];
  for (const sign of [-1, 1]) {
    const pivot = new TransformNode(`leg-pivot-${sign}`, scene);
    pivot.position = new Vector3(0.12 * sign, 0.88 * heightMult, 0);
    pivot.parent = root;
    const leg = MeshBuilder.CreateCylinder(`leg-${sign}`, {
      diameterTop: 0.18,
      diameterBottom: 0.14,
      height: 0.82 * heightMult,
      tessellation: 10,
    }, scene);
    leg.position.y = -0.42 * heightMult;
    leg.material = trouserMat;
    leg.parent = pivot;
    // Foot — flat box at the end.
    const foot = MeshBuilder.CreateBox(`foot-${sign}`, { width: 0.18, height: 0.10, depth: 0.30 }, scene);
    foot.position = new Vector3(0, -0.86 * heightMult, -0.05);
    foot.material = trouserMat;
    foot.parent = pivot;
    legMeshes.push({ pivot, phase: sign > 0 ? Math.PI : 0 });
  }

  // Sigil banner on chest — a small darker quad the sub-faction colour will tint.
  const sigil = MeshBuilder.CreatePlane('sigil', { width: 0.32, height: 0.32 }, scene);
  sigil.position = new Vector3(0, 1.32 * heightMult, -0.17);
  sigil.parent = root;
  const sigilMat = new StandardMaterial('sigilMat', scene);
  sigilMat.diffuseColor = new Color3(0.4, 0.4, 0.4);
  sigilMat.emissiveColor = new Color3(0.1, 0.1, 0.1);
  sigil.material = sigilMat;

  // ── Equipment attachment anchors ─────────────────────────────────
  const headAnchor = new TransformNode('eq-head', scene);
  headAnchor.parent = root;
  headAnchor.position = new Vector3(0, 1.92 * heightMult, 0);

  const backAnchor = new TransformNode('eq-back', scene);
  backAnchor.parent = root;
  backAnchor.position = new Vector3(0, 1.45 * heightMult, 0.18);

  const shouldersAnchor = new TransformNode('eq-shoulders', scene);
  shouldersAnchor.parent = root;
  shouldersAnchor.position = new Vector3(0, 1.6 * heightMult, 0);

  const feetAnchorL = new TransformNode('eq-feet-l', scene);
  feetAnchorL.parent = legMeshes[0].pivot;
  feetAnchorL.position = new Vector3(0, -0.82 * heightMult, 0);
  const feetAnchorR = new TransformNode('eq-feet-r', scene);
  feetAnchorR.parent = legMeshes[1].pivot;
  feetAnchorR.position = new Vector3(0, -0.82 * heightMult, 0);

  let movingState = false;
  let phase = 0;
  // Attack-swing animation timer (0..1 over ~0.4s, plays once).
  let attackT = 0; // -1 = idle, 0..1 = playing

  const meshes: Mesh[] = [];
  root.getChildMeshes(false).forEach((m) => meshes.push(m as Mesh));

  // Apply uniform scale.
  root.scaling = new Vector3(scale, scale, scale);

  // Track currently-equipped meshes per slot for disposal on swap.
  const equipMeshes = new Map<Slot, { meshes: Mesh[]; mats: StandardMaterial[] }>();
  let currentEquipped: Partial<Record<Slot, string>> = {};

  // Cosmetic overlays — separate from gameplay equipment so they layer on top.
  let cosmeticCapeMesh: Mesh | null = null;
  let cosmeticCapeMat: StandardMaterial | null = null;
  let currentCosmeticCape: string | null = null;
  let petRoot: TransformNode | null = null;
  let petMesh: Mesh | null = null;
  let petMat: StandardMaterial | null = null;
  let currentCosmeticPet: string | null = null;
  let petPhase = 0;

  function disposeSlot(slot: Slot) {
    const ent = equipMeshes.get(slot);
    if (!ent) return;
    for (const m of ent.meshes) m.dispose();
    for (const mat of ent.mats) mat.dispose();
    equipMeshes.delete(slot);
  }

  function buildSlotMesh(slot: Slot, item: ItemDef) {
    const tint = item.tint ? Color3.FromHexString(item.tint) : new Color3(0.6, 0.6, 0.6);
    const ms: Mesh[] = [];
    const mats: StandardMaterial[] = [];
    const mat = new StandardMaterial(`eq-${slot}-mat`, scene);
    mat.diffuseColor = tint;
    mat.specularColor = item.rarity === 'legendary' ? new Color3(0.3, 0.25, 0.1) : new Color3(0.08, 0.08, 0.08);
    if (item.rarity === 'epic' || item.rarity === 'legendary') {
      mat.emissiveColor = tint.scale(0.18);
    }
    mats.push(mat);

    if (slot === 'head') {
      const helm = MeshBuilder.CreateSphere('eq-helm', { diameter: 0.38, segments: 14 }, scene);
      helm.scaling = new Vector3(1.05, 0.78, 1.05);
      helm.material = mat;
      helm.parent = headAnchor;
      helm.position = new Vector3(0, 0.02, 0);
      ms.push(helm);
      // Plate helm gets a visor strip.
      if (item.armorClass === 'plate') {
        const visor = MeshBuilder.CreateBox('eq-helm-visor', { width: 0.36, height: 0.06, depth: 0.08 }, scene);
        const visorMat = new StandardMaterial('eq-helm-visor-mat', scene);
        visorMat.diffuseColor = new Color3(0.05, 0.05, 0.05);
        visor.material = visorMat;
        visor.parent = headAnchor;
        visor.position = new Vector3(0, -0.04, -0.16);
        ms.push(visor);
        mats.push(visorMat);
      }
    } else if (slot === 'shoulders') {
      for (const sign of [-1, 1]) {
        const pad = MeshBuilder.CreateSphere(`eq-pauldron-${sign}`, { diameter: 0.28, segments: 12 }, scene);
        pad.scaling = new Vector3(1.0, 0.55, 1.0);
        pad.material = mat;
        pad.parent = root;
        pad.position = new Vector3(0.36 * sign * widthMult, 1.6 * heightMult, 0);
        ms.push(pad);
      }
    } else if (slot === 'chest') {
      // Recolour the torso accent material directly via overlay plate.
      const overlay = MeshBuilder.CreateBox('eq-chest', {
        width: 0.6 * widthMult,
        height: 0.74 * heightMult,
        depth: 0.36,
      }, scene);
      overlay.position.y = 1.3 * heightMult;
      overlay.material = mat;
      overlay.parent = root;
      ms.push(overlay);
    } else if (slot === 'legs') {
      // Slightly larger trouser overlay.
      for (const sign of [-1, 1]) {
        const overlay = MeshBuilder.CreateBox(`eq-leg-${sign}`, {
          width: 0.21,
          height: 0.88 * heightMult,
          depth: 0.22,
        }, scene);
        overlay.position = new Vector3(0.12 * sign, 0.49 * heightMult, 0);
        overlay.material = mat;
        overlay.parent = root;
        ms.push(overlay);
      }
    } else if (slot === 'feet') {
      for (const anchor of [feetAnchorL, feetAnchorR]) {
        const boot = MeshBuilder.CreateBox('eq-boot', { width: 0.22, height: 0.14, depth: 0.32 }, scene);
        boot.material = mat;
        boot.parent = anchor;
        boot.position = new Vector3(0, 0, -0.04);
        ms.push(boot);
      }
    } else if (slot === 'cape') {
      const cape = MeshBuilder.CreatePlane('eq-cape', { width: 0.6 * widthMult, height: 1.1 * heightMult }, scene);
      mat.backFaceCulling = false;
      cape.material = mat;
      cape.parent = backAnchor;
      cape.position = new Vector3(0, -0.55 * heightMult, 0.04);
      cape.rotation.x = 0.18;
      ms.push(cape);
    } else if (slot === 'mainhand') {
      const kind = item.weaponKind ?? 'sword';
      buildWeapon(kind, mat, handAnchors.right, false, ms, mats);
    } else if (slot === 'offhand') {
      const kind = item.weaponKind ?? 'shield';
      buildWeapon(kind, mat, handAnchors.left, true, ms, mats);
    }

    equipMeshes.set(slot, { meshes: ms, mats });
  }

  function buildWeapon(
    kind: string,
    mat: StandardMaterial,
    anchor: TransformNode,
    isOffhand: boolean,
    ms: Mesh[],
    mats: StandardMaterial[],
  ) {
    if (kind === 'sword' || kind === 'dagger') {
      const length = kind === 'dagger' ? 0.45 : 0.95;
      const blade = MeshBuilder.CreateBox('eq-blade', { width: 0.06, height: length, depth: 0.02 }, scene);
      blade.material = mat;
      blade.parent = anchor;
      blade.position = new Vector3(0, length * 0.5, 0);
      ms.push(blade);
      const guard = MeshBuilder.CreateBox('eq-guard', { width: 0.22, height: 0.04, depth: 0.05 }, scene);
      const guardMat = new StandardMaterial('eq-guard-mat', scene);
      guardMat.diffuseColor = new Color3(0.4, 0.32, 0.2);
      guard.material = guardMat;
      guard.parent = anchor;
      guard.position = new Vector3(0, 0, 0);
      ms.push(guard); mats.push(guardMat);
      const grip = MeshBuilder.CreateCylinder('eq-grip', { height: 0.16, diameter: 0.05 }, scene);
      const gripMat = new StandardMaterial('eq-grip-mat', scene);
      gripMat.diffuseColor = new Color3(0.2, 0.13, 0.08);
      grip.material = gripMat;
      grip.parent = anchor;
      grip.position = new Vector3(0, -0.08, 0);
      ms.push(grip); mats.push(gripMat);
    } else if (kind === 'axe' || kind === 'mace') {
      const haft = MeshBuilder.CreateCylinder('eq-haft', { height: 0.7, diameter: 0.05 }, scene);
      const haftMat = new StandardMaterial('eq-haft-mat', scene);
      haftMat.diffuseColor = new Color3(0.25, 0.15, 0.08);
      haft.material = haftMat;
      haft.parent = anchor;
      haft.position = new Vector3(0, 0.35, 0);
      ms.push(haft); mats.push(haftMat);
      const headShape = kind === 'axe'
        ? MeshBuilder.CreateBox('eq-head', { width: 0.22, height: 0.18, depth: 0.05 }, scene)
        : MeshBuilder.CreateSphere('eq-head', { diameter: 0.16, segments: 10 }, scene);
      headShape.material = mat;
      headShape.parent = anchor;
      headShape.position = new Vector3(0, 0.65, 0);
      ms.push(headShape);
    } else if (kind === 'bow') {
      const bow = MeshBuilder.CreateTorus('eq-bow', { diameter: 0.7, thickness: 0.025, tessellation: 16 }, scene);
      bow.scaling = new Vector3(1, 0.5, 0.4);
      bow.material = mat;
      bow.parent = anchor;
      bow.position = new Vector3(0, 0.35, 0);
      bow.rotation.z = Math.PI / 2;
      ms.push(bow);
    } else if (kind === 'staff') {
      const shaft = MeshBuilder.CreateCylinder('eq-staff', { height: 1.4, diameter: 0.05 }, scene);
      shaft.material = mat;
      shaft.parent = anchor;
      shaft.position = new Vector3(0, 0.7, 0);
      ms.push(shaft);
      const orb = MeshBuilder.CreateSphere('eq-staff-orb', { diameter: 0.16, segments: 12 }, scene);
      const orbMat = new StandardMaterial('eq-staff-orb-mat', scene);
      orbMat.diffuseColor = mat.diffuseColor;
      orbMat.emissiveColor = mat.diffuseColor.scale(0.6);
      orb.material = orbMat;
      orb.parent = anchor;
      orb.position = new Vector3(0, 1.4, 0);
      ms.push(orb); mats.push(orbMat);
    } else if (kind === 'shield') {
      const shield = MeshBuilder.CreateCylinder('eq-shield', { height: 0.05, diameter: 0.6, tessellation: 16 }, scene);
      shield.material = mat;
      shield.parent = anchor;
      shield.position = new Vector3(0, 0.25, 0);
      shield.rotation.x = Math.PI / 2;
      ms.push(shield);
      const boss = MeshBuilder.CreateSphere('eq-shield-boss', { diameter: 0.14, segments: 10 }, scene);
      const bossMat = new StandardMaterial('eq-shield-boss-mat', scene);
      bossMat.diffuseColor = mat.diffuseColor.scale(1.3);
      boss.material = bossMat;
      boss.parent = anchor;
      boss.position = new Vector3(0, 0.25, -0.08);
      ms.push(boss); mats.push(bossMat);
    } else if (kind === 'wand') {
      const wand = MeshBuilder.CreateCylinder('eq-wand', { height: 0.4, diameter: 0.03 }, scene);
      wand.material = mat;
      wand.parent = anchor;
      wand.position = new Vector3(0, 0.2, 0);
      ms.push(wand);
      const tip = MeshBuilder.CreateSphere('eq-wand-tip', { diameter: 0.08, segments: 10 }, scene);
      const tipMat = new StandardMaterial('eq-wand-tip-mat', scene);
      tipMat.diffuseColor = mat.diffuseColor;
      tipMat.emissiveColor = mat.diffuseColor.scale(0.7);
      tip.material = tipMat;
      tip.parent = anchor;
      tip.position = new Vector3(0, 0.4, 0);
      ms.push(tip); mats.push(tipMat);
    }
    void isOffhand;
  }

  return {
    root,
    setColours: (skin, hair, accent) => {
      skinMat.diffuseColor = skin;
      hairMat.diffuseColor = hair;
      accentMat.diffuseColor = accent;
      sigilMat.emissiveColor = accent.scale(0.8);
    },
    setMoving: (moving) => {
      movingState = moving;
    },
    update: (dt) => {
      if (movingState) {
        phase += dt * 6;
        for (const a of armMeshes) {
          a.pivot.rotation.x = Math.sin(phase + a.phase) * 0.6;
        }
        for (const l of legMeshes) {
          l.pivot.rotation.x = Math.sin(phase + l.phase) * 0.5;
        }
        // Slight body bob — preserve any externally-driven base height.
      } else {
        // Idle — slow breath bob.
        phase += dt * 1.0;
        for (const a of armMeshes) {
          a.pivot.rotation.x *= 0.92;
        }
        for (const l of legMeshes) {
          l.pivot.rotation.x *= 0.92;
        }
      }
      // Attack swing — overrides right-arm rotation while playing.
      if (attackT >= 0) {
        attackT += dt / 0.4; // ~0.4 second swing
        const t = Math.min(1, attackT);
        // Wind-up (0..0.3) then strike (0.3..1) ease-out.
        const armPivot = armMeshes[0]?.pivot; // sign +1 in our build is the right arm (index 0)
        if (armPivot) {
          // Eased angle: backswing -0.6 → forward +1.4 → relax 0.
          let ang: number;
          if (t < 0.3) {
            ang = -(t / 0.3) * 0.6;
          } else if (t < 0.7) {
            const u = (t - 0.3) / 0.4;
            ang = -0.6 + u * 2.0; // -0.6 → 1.4
          } else {
            const u = (t - 0.7) / 0.3;
            ang = 1.4 * (1 - u);
          }
          armPivot.rotation.x = ang;
        }
        if (t >= 1) attackT = -1;
      }
      // Pet bob — orbits gently around the side.
      if (petRoot) {
        petPhase += dt * 1.6;
        petRoot.position.x = Math.cos(petPhase) * 0.7 * widthMult + 0.3 * widthMult;
        petRoot.position.z = Math.sin(petPhase) * 0.7 + 0.5;
        petRoot.position.y = 0.6 + Math.sin(petPhase * 1.7) * 0.12;
      }
    },
    triggerAttack: () => {
      if (attackT < 0 || attackT > 0.4) attackT = 0;
    },
    setEquipment: (next) => {
      const allSlots: Slot[] = ['head', 'shoulders', 'chest', 'legs', 'feet', 'mainhand', 'offhand', 'cape'];
      for (const slot of allSlots) {
        const prev = currentEquipped[slot];
        const now = next[slot];
        if (prev === now) continue;
        disposeSlot(slot);
        if (now) {
          const item = ITEM_LOOKUP.get(now);
          if (item) buildSlotMesh(slot, item);
        }
      }
      currentEquipped = { ...next };
    },
    setCosmetics: ({ cape, pet }) => {
      // Cosmetic cape — replaces gameplay cape visual.
      if (cape !== currentCosmeticCape) {
        if (cosmeticCapeMesh) { cosmeticCapeMesh.dispose(); cosmeticCapeMesh = null; }
        if (cosmeticCapeMat) { cosmeticCapeMat.dispose(); cosmeticCapeMat = null; }
        if (cape) {
          const def = COSMETIC_LOOKUP.get(cape);
          if (def && def.category === 'cape') {
            const tint = Color3.FromHexString(def.tint);
            cosmeticCapeMat = new StandardMaterial('cos-cape-mat', scene);
            cosmeticCapeMat.diffuseColor = tint;
            cosmeticCapeMat.emissiveColor = tint.scale(def.rarity === 'legendary' ? 0.3 : def.rarity === 'epic' ? 0.18 : 0.08);
            cosmeticCapeMat.backFaceCulling = false;
            const capeMesh = MeshBuilder.CreatePlane('cos-cape', { width: 0.7 * widthMult, height: 1.25 * heightMult }, scene);
            capeMesh.material = cosmeticCapeMat;
            capeMesh.parent = backAnchor;
            capeMesh.position = new Vector3(0, -0.65 * heightMult, 0.06);
            capeMesh.rotation.x = 0.18;
            cosmeticCapeMesh = capeMesh;
          }
        }
        currentCosmeticCape = cape;
      }

      // Pet — small floating sphere following the character.
      if (pet !== currentCosmeticPet) {
        if (petMesh) { petMesh.dispose(); petMesh = null; }
        if (petMat) { petMat.dispose(); petMat = null; }
        if (petRoot) { petRoot.dispose(); petRoot = null; }
        if (pet) {
          const def = COSMETIC_LOOKUP.get(pet);
          if (def && def.category === 'pet') {
            const tint = Color3.FromHexString(def.tint);
            petRoot = new TransformNode('pet-root', scene);
            petRoot.parent = root;
            petRoot.position = new Vector3(0.9 * widthMult, 0.6, 0.9);
            petMat = new StandardMaterial('pet-mat', scene);
            petMat.diffuseColor = tint;
            petMat.emissiveColor = tint.scale(0.4);
            const sphere = MeshBuilder.CreateSphere('pet', { diameter: 0.32, segments: 12 }, scene);
            sphere.scaling = new Vector3(1, 0.85, 1);
            sphere.material = petMat;
            sphere.parent = petRoot;
            petMesh = sphere;
          }
        }
        currentCosmeticPet = pet;
      }
    },
    dispose: () => {
      for (const slot of Array.from(equipMeshes.keys())) disposeSlot(slot);
      if (cosmeticCapeMesh) cosmeticCapeMesh.dispose();
      if (cosmeticCapeMat) cosmeticCapeMat.dispose();
      if (petMesh) petMesh.dispose();
      if (petMat) petMat.dispose();
      if (petRoot) petRoot.dispose();
      root.dispose(false, true);
      skinMat.dispose();
      hairMat.dispose();
      accentMat.dispose();
      trouserMat.dispose();
      sigilMat.dispose();
      eyeMat.dispose();
      mouthMat.dispose();
    },
  };
}
